/**
 * SELECTIONCYCLING transient state (B12).
 *
 * Holds two short-lived bits of UI state that are not part of the
 * document model and therefore must NOT participate in undo / redo /
 * audit / DXF round-trip:
 *
 *   - `stackedCandidates` — the set of entity ids currently under the
 *     cursor (≥2 means the cursor is on a stacked region). Updated on
 *     every pointermove the canvas observes; cleared on pointerleave.
 *   - `cycleIndex` — Shift+Space rotation pointer. Increments every
 *     time the user presses Shift+Space; the keyboard shortcut handler
 *     reads `(stackedCandidates[cycleIndex % len])` to pick the next
 *     candidate. Reset to 0 whenever the candidate set changes.
 *   - `dialog` — when SELECTIONCYCLING=2 and the user clicks on a
 *     stacked region, App.tsx mounts <SelectionCyclingDialog> at the
 *     stored screen position with the stored candidate ids. `null`
 *     means the dialog is hidden.
 *
 * Why a separate store (vs editorStore.selection):
 *   - hover-frequency writes (pointermove) — see hoverState.ts §1
 *   - cycleIndex is per-key-press, not part of the document
 *   - the dialog is a popup, not a redux-style modal flag
 *
 * AutoCAD parity:
 *   SELECTIONCYCLING=0 → no badge, no dialog (standard pick)
 *   SELECTIONCYCLING=1 → badge cursor adornment when ≥2 candidates;
 *                        single click still picks topmost (small bbox)
 *   SELECTIONCYCLING=2 → badge + dialog popup on click
 *   Shift+Space — silently rotate to the next stacked candidate
 *                 regardless of SELECTIONCYCLING value
 */

import { create } from "zustand";

export interface StackedCandidate {
  /** Entity id (stable across renders). */
  readonly id: string;
  /** EntityPreview-level type (for the dialog row label). */
  readonly type: string;
  /**
   * Layer name (for the dialog row's secondary line). Empty string is
   * fine — the dialog uses "—" as fallback.
   */
  readonly layer: string;
}

export interface SelectionCyclingDialogState {
  /** CSS px coords relative to the workspace, where the popup anchors. */
  readonly screen: { x: number; y: number };
  /** Candidates to render (in z-order, smallest-bbox first). */
  readonly candidates: ReadonlyArray<StackedCandidate>;
  /** Active space id — passed to the parent's onSelect callback. */
  readonly spaceId: string;
}

export interface SelectionCyclingState {
  /** Current candidates under the cursor. Empty when none / one entity. */
  stackedCandidates: ReadonlyArray<StackedCandidate>;
  /** Cycle pointer for Shift+Space silent rotation. */
  cycleIndex: number;
  /** Active popup dialog or null when hidden. */
  dialog: SelectionCyclingDialogState | null;
  setStackedCandidates: (
    candidates: ReadonlyArray<StackedCandidate>,
  ) => void;
  /** Increment cycleIndex; returns the candidate id at the next position. */
  advanceCycle: () => string | null;
  resetCycle: () => void;
  openDialog: (state: SelectionCyclingDialogState) => void;
  closeDialog: () => void;
}

function shallowIdEqual(
  a: ReadonlyArray<StackedCandidate>,
  b: ReadonlyArray<StackedCandidate>,
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]!.id !== b[i]!.id) return false;
  }
  return true;
}

export const useSelectionCyclingStore = create<SelectionCyclingState>(
  (set, get) => ({
    stackedCandidates: [],
    cycleIndex: 0,
    dialog: null,
    setStackedCandidates: (candidates) =>
      set((prev) => {
        if (shallowIdEqual(prev.stackedCandidates, candidates)) return prev;
        // Candidate set changed — reset the cycle pointer so the next
        // Shift+Space starts from the topmost entry.
        return { ...prev, stackedCandidates: candidates, cycleIndex: 0 };
      }),
    advanceCycle: () => {
      const { stackedCandidates, cycleIndex } = get();
      if (stackedCandidates.length === 0) return null;
      const nextIndex = (cycleIndex + 1) % stackedCandidates.length;
      set({ cycleIndex: nextIndex });
      return stackedCandidates[nextIndex]!.id;
    },
    resetCycle: () => set({ cycleIndex: 0 }),
    openDialog: (state) => set({ dialog: state }),
    closeDialog: () => set({ dialog: null }),
  }),
);

/** Pure getter for non-React callers (keyboard shortcut handler). */
export function getSelectionCyclingState(): SelectionCyclingState {
  return useSelectionCyclingStore.getState();
}
